import { z } from 'zod'

import {
  isNepalMobile,
  isTenDigitPhone,
  looksLikePhone,
  NEPAL_MOBILE_MESSAGE,
  PHONE_MESSAGE,
  toLocalDigits,
} from '@/lib/phone'

/**
 * Boundary validation for the auth forms, and for what /api/v5/auth/ returns.
 *
 * Messages are written to be read by a person mid-form, not to describe the
 * rule that failed — "Enter your 10-digit mobile number" beats "invalid".
 */

/**
 * What a member types into "Phone number or email", as the backend matches it.
 *
 * mm-be looks the identifier up with an exact `phone=` or `email=` match
 * (services/auth/repository.py), and sign-up stores the bare 10-digit number.
 * So "+977 981-234 5678" would miss an account that "9812345678" finds. Anything
 * with an "@" is an email and goes through only trimmed; anything else loses
 * its separators and a leading +977 / 977. A digits-only value that is not
 * ten digits is rejected here rather than sent to miss on the backend.
 */
const identifierSchema = z
  .string()
  .trim()
  .min(1, 'Enter your phone number or email')
  .refine(
    (raw) => raw.includes('@') || !looksLikePhone(raw) || isTenDigitPhone(toLocalDigits(raw)),
    {
      message: PHONE_MESSAGE,
    },
  )
  .transform((raw) => (raw.includes('@') || !looksLikePhone(raw) ? raw : toLocalDigits(raw)))

/** The message a "Phone number or email" value earns now, if any — for blur. */
export const identifierError = (value: string): string | undefined => {
  const result = identifierSchema.safeParse(value)
  return result.success ? undefined : result.error.issues[0]?.message
}

export const signInSchema = z.object({
  identifier: identifierSchema,
  password: z.string().min(1, 'Enter your password'),
})

/** Which detail a new member signs up with. Exactly one is sent. */
export type SignUpContact = 'phone' | 'email'

const signUpBase = z.object({
  fullName: z.string().trim().min(2, 'Enter your full name'),
  password: z.string().min(8, 'Use at least 8 characters'),
})

/**
 * A union on `contact`, so a parsed sign-up carries the phone OR the email and
 * never both: the object schemas strip the other key, which is what keeps the
 * unchosen field out of the request body.
 */
export const signUpSchema = z.discriminatedUnion('contact', [
  signUpBase.extend({
    contact: z.literal('phone'),
    phone: z.string().trim().refine(isNepalMobile, NEPAL_MOBILE_MESSAGE),
  }),
  signUpBase.extend({
    contact: z.literal('email'),
    email: z.email('Enter a valid email address'),
  }),
])

/**
 * The code we text after sign-up. Exactly six digits: the backend rejects
 * anything else with a VALIDATION_ERROR, and saying so here costs a member
 * no round trip.
 */
export const otpSchema = z.object({
  otp: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code we sent you'),
})

/** Forgot password takes the same identifier sign-in does. */
export const forgotPasswordSchema = z.object({
  identifier: identifierSchema,
})

export type SignInInput = z.infer<typeof signInSchema>
export type SignUpInput = z.infer<typeof signUpSchema>
export type OtpInput = z.infer<typeof otpSchema>
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>

/*
 * ── What /api/v5/auth/ returns ─────────────────────────────────────────────
 *
 * Wire shapes, so snake_case: these describe the backend's `data`, not the
 * app's vocabulary. Services parse with them and map to app types (`Me`,
 * `SignInResult`) before anything reaches a screen.
 *
 * This file may not import the generated `@/api/schema` types, so the check
 * that these schemas still describe the contract lives in the services, as a
 * `satisfies` on every parse — a regenerated schema that renames or retypes a
 * field fails `pnpm typecheck` there.
 *
 * Every field the backend sends is listed, even ones nothing reads yet. The
 * check requires it (a parse result missing a contract field does not satisfy
 * the contract type), and it is what lets a rename on the backend surface as
 * a compile error rather than a field that is quietly always undefined.
 */

export const authUserSchema = z.object({
  id: z.number().int(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** May be blank: the backend splits "Full name" on whitespace, and older
   *  and social accounts can have neither half. */
  first_name: z.string(),
  last_name: z.string(),
  otp_verified: z.boolean(),
})

/**
 * The member's live subscription, as the backend resolves it.
 *
 * `tier` stays a plain string here on purpose. The backend owns the tier list
 * (`free | bull | shark` today) and may add one; an enum would turn a new tier
 * into a failed sign-in. The one place tiers become app plans is
 * `planFromTier` in the account service, which maps an unknown tier to free.
 */
export const authSubscriptionSchema = z.object({
  title: z.string(),
  tier: z.string(),
  is_active: z.boolean(),
  expires_on: z.string().nullable(),
})

export const authProfileSchema = z.object({
  country: z.string().nullable(),
  country_code: z.string().nullable(),
  phone_verified: z.boolean(),
})

/**
 * The web app logs in in cookie mode (api/auth-cookie.ts), so `refresh` is
 * null: the refresh token arrives as an HttpOnly cookie instead. Accepted as a
 * string too, and never read, so a backend that ignored the header would cost
 * the member a sign-in once their access token lapses, not the sign-in itself.
 */
export const authTokensSchema = z.object({
  access: z.string().min(1),
  refresh: z.string().nullable(),
})

/** POST /auth/login/ — and, when it is wired, POST /auth/social/. */
export const loginResponseSchema = authTokensSchema.extend({
  user: authUserSchema,
  subscription: authSubscriptionSchema,
})

/**
 * GET /auth/me/. `profile` is null until the member has one. `has_password`
 * is false for an account made by social sign-in, which confirms a password
 * change or a deletion by signing in with its provider again instead.
 */
export const meResponseSchema = z.object({
  user: authUserSchema,
  profile: authProfileSchema.nullable(),
  subscription: authSubscriptionSchema,
  has_password: z.boolean(),
})

/** POST /auth/register/ — the account exists but is unverified; no tokens. */
export const registerResponseSchema = z.object({
  user_id: z.number().int(),
})

/**
 * POST /auth/otp/verify/. A literal `true`: a wrong code arrives as an
 * OTP_INVALID error, so a 200 that says `verified: false` would be a contract
 * break, and treating it as success would send an unverified member on to a
 * login that then fails with ACCOUNT_NOT_VERIFIED.
 */
export const verifiedResponseSchema = z.object({
  verified: z.literal(true),
})

/**
 * POST /auth/otp/resend/ and /auth/password-reset/.
 *
 * Deliberately `boolean`, not `true`: password reset answers `sent: true` for
 * an identifier that has no account, so that the endpoint cannot be used to
 * find out who is a member. The flag carries no information the UI can act on.
 */
export const sentResponseSchema = z.object({
  sent: z.boolean(),
})

/**
 * POST /auth/password-reset/confirm/ and /auth/password/change/. A literal
 * `true`, as `verifiedResponseSchema` is: a refusal arrives as an error, so a
 * 200 saying `changed: false` would be a contract break, not a wrong password.
 */
export const passwordChangedSchema = z.object({
  changed: z.literal(true),
})

export type AuthTokens = z.infer<typeof authTokensSchema>
export type AuthUser = z.infer<typeof authUserSchema>
export type AuthSubscription = z.infer<typeof authSubscriptionSchema>

/**
 * First error per field, keyed by field name.
 *
 * `flatten().fieldErrors` types every key as `string[] | undefined`, and under
 * `exactOptionalPropertyTypes` writing that undefined into a
 * `Partial<Record<K, string>>` is a type error — an absent key and a key set to
 * undefined are not the same thing. Building the object by omission keeps the
 * distinction the compiler is asking for.
 *
 * First message only: showing three complaints under one input is noise when
 * fixing the first usually clears the rest.
 */
export const fieldErrorsFrom = <K extends string>(
  error: z.ZodError,
): Partial<Record<K, string>> => {
  const out: Partial<Record<K, string>> = {}
  for (const issue of error.issues) {
    const key = issue.path[0]
    if (typeof key === 'string' && !(key in out)) out[key as K] = issue.message
  }
  return out
}
